/**
 * CameraManager - kapselt eine Kamera mit:
 *  - Source-Stream (Master in max. Auflösung ODER direkt in Ziel-Auflösung)
 *  - Hidden Video-Element zum Rendern der Source
 *  - Canvas + RAF-Loop zum Croppen nach ROI
 *  - canvas.captureStream() liefert den abgeleiteten MediaStream für WebRTC
 *
 * Pro Kamera: ein CameraPipeline-Objekt. Beim Swap wechselt die Ziel-Auflösung
 * (Canvas-Größe), die Source bleibt stabil.
 */

import {
  buildVideoConstraints,
  CameraRole,
  getActualTrackSettings,
  openMasterStream,
  RESOLUTIONS,
} from './videoSettings';

export interface RoiBox {
  x: number; // 0..1
  y: number; // 0..1
  w: number; // 0..1
  h: number; // 0..1
}

export const ROI_FULL: RoiBox = { x: 0, y: 0, w: 1, h: 1 };

export function clampRoi(roi: RoiBox): RoiBox {
  const w = Math.max(0.05, Math.min(1, roi.w));
  const h = Math.max(0.05, Math.min(1, roi.h));
  const x = Math.max(0, Math.min(1 - w, roi.x));
  const y = Math.max(0, Math.min(1 - h, roi.y));
  return { x, y, w, h };
}

export interface CameraCapabilities {
  cameraRole: CameraRole;
  deviceId: string;
  label: string;
  masterTrack: boolean;
  sourceWidth: number;
  sourceHeight: number;
  outputWidth: number;
  outputHeight: number;
  /** Bis zu welchem Faktor noch detailerhaltend (nur sinnvoll bei masterTrack=true). */
  maxDetailZoom: number;
}

export interface PipelineOpts {
  cameraRole: CameraRole;
  deviceId: string;
  label: string;
  masterTrack: boolean;
  /** Ziel-Output-Auflösung (z.B. {1280,720}) */
  outputWidth: number;
  outputHeight: number;
  outputFps: number;
  /** Welle 8c-pre6: fps des Master-Streams (Bandbreite USB 2.0). Default 10.
   *  Scans greifen weiterhin ein volles Einzelbild ab — fps betrifft nur den Livestream. */
  masterFps?: number;
  /** Welle 8c-pre45: bei non-master Tracks die Kamera im nativen Seitenverhältnis
   *  öffnen (volles Sichtfeld), statt width×height auf 16:9 zu zwingen. */
  preserveSourceAspect?: boolean;
}

export class CameraPipeline {
  readonly cameraRole: CameraRole;
  readonly deviceId: string;
  readonly label: string;
  readonly masterTrack: boolean;
  outputWidth: number;
  outputHeight: number;
  outputFps: number;
  readonly masterFps: number;
  readonly preserveSourceAspect: boolean;

  /** Source: getUserMedia-Stream. Bei masterTrack=true: max-Auflösung. Sonst: Ziel-Auflösung. */
  sourceStream: MediaStream | null = null;
  /** Hidden Video-Element für RAF-Rendering. */
  videoEl: HTMLVideoElement | null = null;
  /** Canvas für ROI-Crop. */
  canvasEl: HTMLCanvasElement | null = null;
  /** Abgeleiteter MediaStream via canvas.captureStream. */
  outputStream: MediaStream | null = null;
  /** Output-Track für WebRTC. */
  outputTrack: MediaStreamTrack | null = null;

  /** Aktueller ROI für diese Kamera. */
  roi: RoiBox = { ...ROI_FULL };

  /** Source-Dimensionen (nach getUserMedia). */
  sourceWidth = 0;
  sourceHeight = 0;
  sourceFps = 0;

  private rafId: number | null = null;
  private stopped = false;

  constructor(opts: PipelineOpts) {
    this.cameraRole = opts.cameraRole;
    this.deviceId = opts.deviceId;
    this.label = opts.label;
    this.masterTrack = opts.masterTrack;
    this.outputWidth = opts.outputWidth;
    this.outputHeight = opts.outputHeight;
    this.outputFps = opts.outputFps;
    this.masterFps = opts.masterFps ?? 10;
    this.preserveSourceAspect = opts.preserveSourceAspect ?? false;
  }

  async start(): Promise<void> {
    if (this.masterTrack) {
      this.sourceStream = await openMasterStream(this.deviceId, this.masterFps);
    } else {
      // Ohne Master-Track: Source = Ziel-Auflösung
      this.sourceStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: buildVideoConstraints(
          this.deviceId,
          this.matchResolutionKey(this.outputWidth),
          this.outputFps,
          this.preserveSourceAspect,
        ),
      });
    }

    const track = this.sourceStream.getVideoTracks()[0];
    const s = track.getSettings();
    this.sourceWidth = s.width ?? 0;
    this.sourceHeight = s.height ?? 0;
    this.sourceFps = s.frameRate ?? 30;

    console.log(
      `[cameraPipeline ${this.cameraRole}] Source ${this.sourceWidth}x${this.sourceHeight}@${this.sourceFps}, masterTrack=${this.masterTrack}`,
    );

    // Welle 5a: Canvas-Größe an Source-Aspect anpassen.
    // Wenn Source Portrait ist (z.B. 1080x1920), muss der Canvas auch Portrait sein,
    // sonst wird das Bild gestreckt. Wir behalten die "kürzere Seite" aus den Output-
    // Constraints (typisch 720 für 720p-Profil) und drehen die Orientierung wie die Source.
    const corrected = this.computeAspectCorrectCanvas(
      this.outputWidth, this.outputHeight, this.sourceWidth, this.sourceHeight,
    );
    this.outputWidth = corrected.w;
    this.outputHeight = corrected.h;
    console.log(
      `[cameraPipeline ${this.cameraRole}] Canvas-Output ${this.outputWidth}x${this.outputHeight} (aspect-korrigiert)`,
    );

    // Hidden Video-Element
    this.videoEl = document.createElement('video');
    this.videoEl.autoplay = true;
    this.videoEl.muted = true;
    this.videoEl.playsInline = true;
    this.videoEl.srcObject = this.sourceStream;
    this.videoEl.style.position = 'absolute';
    this.videoEl.style.left = '-10000px';
    this.videoEl.style.width = '1px';
    this.videoEl.style.height = '1px';
    document.body.appendChild(this.videoEl);
    await this.videoEl.play().catch((e) => console.warn(`[cameraPipeline ${this.cameraRole}] play fail`, e));

    // Canvas
    this.canvasEl = document.createElement('canvas');
    this.canvasEl.width = this.outputWidth;
    this.canvasEl.height = this.outputHeight;

    // captureStream
    const out = (this.canvasEl as any).captureStream(this.outputFps) as MediaStream;
    this.outputStream = out;
    this.outputTrack = out.getVideoTracks()[0];

    // Welle 8c-pre27: Encoder-Hinweis. Eine Canvas-captureStream-Quelle wird vom
    // Encoder sonst evtl. wie Bildschirminhalt behandelt — in Kombination mit dem
    // Encoded-Transform führt das (nur auf der Stele beobachtet) zu Nur-Keyframes.
    // 'motion' signalisiert Vollbewegtbild wie eine Kamera → normale Inter-Frame-
    // Prädiktion, statt jeden Frame als Keyframe zu kodieren.
    try { this.outputTrack.contentHint = 'motion'; } catch { /* ignore */ }

    this.startRenderLoop();
  }

  /** Welle 8c-pre27: Roher Kamera-Track (vor Canvas/ROI) — nur für die Diagnose
   *  ?rawcam=1, um zu bestätigen, ob die Canvas-Quelle die Nur-Keyframes verursacht. */
  getRawSourceTrack(): MediaStreamTrack | null {
    return this.sourceStream?.getVideoTracks()[0] ?? null;
  }

  private matchResolutionKey(width: number): string {
    if (width >= 1900) return '1080p';
    if (width >= 1200) return '720p';
    if (width >= 800) return '480p';
    if (width >= 600) return '360p';
    return '240p';
  }

  /**
   * Berechnet die Canvas-Größe so, dass sie das Source-Aspect-Ratio respektiert.
   * Die "kürzere Seite" des Targets wird übernommen (z.B. 720 bei 720p-Profil),
   * die längere wird aus dem Source-Aspect abgeleitet.
   * - Source landscape (z.B. 3264x2160): Canvas landscape, lange Seite = 720*aspect
   * - Source portrait (z.B. 1080x1920): Canvas portrait, lange Seite = 720/aspect
   */
  private computeAspectCorrectCanvas(
    targetW: number, targetH: number,
    sourceW: number, sourceH: number,
  ): { w: number; h: number } {
    if (sourceW === 0 || sourceH === 0) return { w: targetW, h: targetH };
    const sourceAspect = sourceW / sourceH;
    const targetShort = Math.min(targetW, targetH); // z.B. 720 aus 1280x720
    if (sourceAspect >= 1) {
      // Landscape oder quadratisch
      return { w: Math.round(targetShort * sourceAspect), h: targetShort };
    } else {
      // Portrait
      return { w: targetShort, h: Math.round(targetShort / sourceAspect) };
    }
  }

  private startRenderLoop() {
    const ctx = this.canvasEl!.getContext('2d', { alpha: false });
    if (!ctx) {
      console.error(`[cameraPipeline ${this.cameraRole}] kein 2d-Context`);
      return;
    }

    let lastDraw = 0;
    const targetInterval = 1000 / this.outputFps;

    const draw = (ts: number) => {
      if (this.stopped) return;
      this.rafId = requestAnimationFrame(draw);

      if (ts - lastDraw < targetInterval - 1) return;
      lastDraw = ts;

      const v = this.videoEl;
      const c = this.canvasEl;
      if (!v || !c || v.videoWidth === 0 || v.videoHeight === 0) return;

      const sw = v.videoWidth;
      const sh = v.videoHeight;
      const cw = c.width;
      const ch = c.height;

      const sx = this.roi.x * sw;
      const sy = this.roi.y * sh;
      const sWidth = this.roi.w * sw;
      const sHeight = this.roi.h * sh;

      try {
        ctx.drawImage(v, sx, sy, sWidth, sHeight, 0, 0, cw, ch);
      } catch (e) {
        // ignore (z.B. wenn videoEl gerade weg)
      }
    };

    this.rafId = requestAnimationFrame(draw);
  }

  /** Output-Auflösung wechseln (z.B. beim Profil-Swap). Aspect-korrekt. */
  setOutputResolution(width: number, height: number, fps?: number): void {
    if (!this.canvasEl) return;
    if (fps && fps !== this.outputFps) {
      this.outputFps = fps;
    }
    const corrected = this.computeAspectCorrectCanvas(
      width, height, this.sourceWidth, this.sourceHeight,
    );
    this.outputWidth = corrected.w;
    this.outputHeight = corrected.h;
    this.canvasEl.width = corrected.w;
    this.canvasEl.height = corrected.h;
    console.log(
      `[cameraPipeline ${this.cameraRole}] Output umgestellt auf ${corrected.w}x${corrected.h}@${this.outputFps} (aspect-korrigiert aus ${width}x${height})`,
    );
  }

  setRoi(roi: RoiBox): void {
    this.roi = clampRoi(roi);
  }

  resetRoi(): void {
    this.roi = { ...ROI_FULL };
  }

  /** Errechnet den maximal sinnvollen Detail-Zoom (kurze Seite vergleichen, aspect-agnostisch). */
  getMaxDetailZoom(): number {
    if (!this.masterTrack) return 1;
    if (this.sourceWidth === 0 || this.outputWidth === 0) return 1;
    const sourceShort = Math.min(this.sourceWidth, this.sourceHeight);
    const outputShort = Math.min(this.outputWidth, this.outputHeight);
    if (outputShort === 0) return 1;
    return Math.max(1, sourceShort / outputShort);
  }

  /** Snapshot des aktuellen Master-Frames (volle Source-Auflösung). */
  async snapshot(format: 'image/jpeg' | 'image/webp' | 'image/png' = 'image/jpeg', quality = 0.92): Promise<Blob | null> {
    const v = this.videoEl;
    if (!v || v.videoWidth === 0) return null;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d', { alpha: false });
    if (!ctx) return null;
    ctx.drawImage(v, 0, 0);
    return await new Promise((resolve) => c.toBlob(resolve, format, quality));
  }

  getCapabilities(): CameraCapabilities {
    return {
      cameraRole: this.cameraRole,
      deviceId: this.deviceId,
      label: this.label,
      masterTrack: this.masterTrack,
      sourceWidth: this.sourceWidth,
      sourceHeight: this.sourceHeight,
      outputWidth: this.outputWidth,
      outputHeight: this.outputHeight,
      maxDetailZoom: Number(this.getMaxDetailZoom().toFixed(2)),
    };
  }

  stop(): void {
    this.stopped = true;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    try { this.sourceStream?.getTracks().forEach((t) => t.stop()); } catch {}
    try { this.outputStream?.getTracks().forEach((t) => t.stop()); } catch {}
    if (this.videoEl) {
      try { this.videoEl.srcObject = null; } catch {}
      try { this.videoEl.remove(); } catch {}
    }
    this.sourceStream = null;
    this.outputStream = null;
    this.outputTrack = null;
    this.videoEl = null;
    this.canvasEl = null;
  }
}
